import { Screen, useNavigate } from "flemo";
import { XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";

const CONTACT_EMAIL = "support@case-d.com";

type PolicyItem = {
  title: string;
  description?: string;
  content?: Array<string | string[]>;
  footer?: string;
};

const COLLECTED_INFO: PolicyItem[] = [
  {
    title: "계정 정보",
    description:
      "본 서비스는 로그인 시 Supabase Authentication을 통해 다음 정보를 수집합니다. 수집된 정보는 서비스 이용자 식별 및 계정 관리에 사용됩니다.",
    content: ["이메일 주소", "프로필 이름 및 사진 (소셜 로그인 사용 시)", "고유 사용자 식별자(UID)"],
    footer: "계정 인증은 Supabase에서 제공하는 인증 서비스를 사용합니다.",
  },
  {
    title: "학습 데이터",
    description:
      "본 서비스는 학습 기능 제공을 위해 다음 데이터를 저장합니다. 수집된 정보는 플래시카드 학습 및 테스트 기능 제공, 학습 진도 추적에 사용됩니다.",
    content: [
      "단어 목록 및 학습 진도",
      "플래시카드 정답/오답 이력",
      "테스트 결과 및 마스터리 상태",
      "즐겨찾기 단어 목록",
    ],
  },
  {
    title: "결제 정보",
    description:
      "유료 구독(Plus) 이용 시 다음 정보가 처리됩니다. 실제 결제는 Apple App Store 또는 Google Play를 통해 이루어지며, 서비스 제공자는 카드 번호 등 민감한 결제 수단 정보를 직접 수집하지 않습니다.",
    content: ["구독 상태 및 유형 (월간/연간)", "구독 시작일 및 만료일", "앱 스토어 거래 식별자"],
  },
  {
    title: "이용 데이터",
    description:
      "본 서비스는 서비스 이용 시 아래와 같은 데이터를 수집합니다. 수집된 정보는 서비스 개선 및 오류 분석에 사용됩니다.",
    content: [
      "기기 정보",
      ["OS 버전, 브라우저 정보, IP 주소"],
      "앱 정보",
      ["서비스 버전, 사용 시작일"],
      "로그 정보",
      ["오류 로그, 충돌 로그, 성능 데이터"],
    ],
  },
  {
    title: "광고 및 추적 데이터",
    description:
      "본 서비스는 맞춤 광고 제공을 위해 Apple의 앱 추적 투명성(ATT) 프레임워크를 통해 이용자의 동의를 구한 후 다음 정보를 수집할 수 있습니다. 이용자는 기기 설정에서 언제든 추적 허용을 철회할 수 있으며, Plus 구독자는 광고가 표시되지 않습니다.",
    content: [
      "광고 식별자 (IDFA)",
      ["기기에 할당된 고유 광고 식별자로, 이용자의 동의 하에 맞춤 광고 제공 및 광고 성과 측정에 사용됩니다"],
      "광고 상호작용 데이터",
      ["광고 노출 횟수, 클릭 여부 등 광고 성과 측정을 위한 데이터"],
    ],
    footer: "추적을 허용하지 않는 경우에도 광고가 표시될 수 있으나, 맞춤형이 아닌 일반 광고가 제공됩니다.",
  },
  {
    title: "피드백 및 문의 정보",
    description:
      "이용자가 서비스에 대한 피드백이나 문의를 위해 서비스 제공자에게 연락하는 경우, 다음 정보가 수집될 수 있습니다. 수집된 정보는 문의 답변 및 서비스 개선에 사용됩니다.",
    content: ["이름", "이메일 주소", "문의 내용"],
  },
];

const THIRD_PARTY_SERVICES: PolicyItem[] = [
  {
    title: "Supabase",
    description: "계정 인증, 데이터베이스, 파일 저장소를 제공합니다.",
    content: ["수집 정보: 계정 정보, 학습 데이터, 프로필 이미지"],
  },
  {
    title: "Sentry",
    description: "오류 추적 및 성능 모니터링을 위해 사용됩니다.",
    content: ["수집 정보: 오류 로그, 기기 정보, 사용자 식별자"],
  },
  {
    title: "Google Analytics (Firebase)",
    description: "서비스 이용 패턴 분석을 위해 사용됩니다.",
    content: ["수집 정보: 페이지 방문 기록, 이벤트 데이터, 기기 정보"],
  },
  {
    title: "PostHog",
    description: "제품 분석 및 사용자 행동 분석을 위해 사용됩니다.",
    content: ["수집 정보: 페이지 방문 기록, 이벤트 데이터, 기기 정보"],
  },
  {
    title: "Mixpanel",
    description: "사용자 행동 분석 및 서비스 개선을 위해 사용됩니다.",
    content: ["수집 정보: 이벤트 데이터, 사용자 속성"],
  },
  {
    title: "Vercel",
    description: "서비스 호스팅, 웹 분석 및 성능 측정을 위해 사용됩니다.",
    content: ["수집 정보: 페이지 방문 기록, 성능 데이터"],
  },
  {
    title: "Google AdMob",
    description: "광고 제공 및 광고 성과 측정을 위해 사용됩니다.",
    content: ["수집 정보: 광고 식별자(IDFA), 광고 노출·클릭 데이터, 기기 정보"],
  },
  {
    title: "RevenueCat",
    description: "인앱 결제 및 구독 관리를 위해 사용됩니다.",
    content: ["수집 정보: 구독 상태, 거래 식별자"],
  },
];

function PolicySection({ title, description, content, footer }: PolicyItem) {
  return (
    <div>
      <h3 className="mb-2 text-lg font-bold">{title}</h3>
      <hr className="mb-4 border-border" />
      <div className="flex flex-col gap-3 text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul_li]:py-1 [&_ul_ul]:list-[circle]">
        {description && <p>{description}</p>}

        {content && (
          <ul>
            {content.map(item =>
              typeof item === "string" ? (
                <li key={item}>{item}</li>
              ) : (
                <ul key={item.join(",")}>
                  {item.map(detail => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              ),
            )}
          </ul>
        )}

        {footer && <p className="text-muted-foreground">{footer}</p>}
      </div>
    </div>
  );
}

export function PrivacyPage() {
  const { pop } = useNavigate();

  return (
    <Screen>
      <main className="mx-auto flex w-full max-w-3xl flex-col p-6 pb-16 overflow-y-auto overscroll-none scrollbar-hidden pt-safe">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-2 pt-2">
          <h1 className="text-2xl font-bold">개인정보 처리방침</h1>

          <Button variant="ghost" size="icon" onClick={pop}>
            <XIcon className="size-5 text-muted-foreground" />
          </Button>
        </div>

        {/* 설명 */}
        <p className="mb-10 text-sm leading-relaxed text-muted-foreground">
          TOBO(이하 &quot;서비스&quot;)는 Case D(이하 &quot;서비스 제공자&quot;)가 제공하는 단어 학습 퀴즈 서비스입니다.
          본 방침은 서비스가 이용자의 개인정보를 어떻게 수집·이용·보호하는지 설명하며, 이용자가 가진 권리와 그 행사
          방법에 대한 정보를 제공합니다.
        </p>

        {/* 개인정보 처리방침 내용 */}
        <div className="flex flex-col gap-10">
          {/* 수집하는 개인정보 항목 */}
          <section>
            <h2 className="mb-4 text-2xl font-bold">수집하는 개인정보 항목</h2>

            <div className="flex flex-col gap-6">
              {COLLECTED_INFO.map(item => (
                <PolicySection key={item.title} {...item} />
              ))}
            </div>
          </section>

          {/* 개인정보의 이용 목적 */}
          <section>
            <h2 className="mb-4 text-2xl font-bold">개인정보의 이용 목적</h2>
            <div className="flex flex-col gap-3 text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul_li]:py-1">
              <ul>
                <li>회원 가입 및 관리 (본인 확인, 계정 인증)</li>
                <li>학습 서비스 제공 (플래시카드, 테스트, 진도 관리)</li>
                <li>유료 구독 서비스 제공 및 결제 처리</li>
                <li>서비스 개선 및 신규 기능 개발을 위한 통계 분석</li>
                <li>이용자 맞춤 광고 제공 및 광고 성과 측정 (이용자 동의 시)</li>
                <li>오류 탐지, 보안 위협 방지 및 서비스 안정성 확보</li>
                <li>고객 문의 대응 및 공지사항 전달</li>
              </ul>
            </div>
          </section>

          {/* 제3자 제공 및 위탁 */}
          <section>
            <h2 className="mb-4 text-2xl font-bold">제3자 서비스 이용</h2>
            <p className="mb-6 text-sm leading-relaxed">
              서비스 제공자는 서비스 운영을 위해 다음과 같은 제3자 서비스를 이용합니다. 각 서비스는 해당 서비스 제공자의
              개인정보처리방침에 따라 데이터를 처리합니다.
            </p>

            <div className="flex flex-col gap-6">
              {THIRD_PARTY_SERVICES.map(item => (
                <PolicySection key={item.title} {...item} />
              ))}
            </div>
          </section>

          {/* 개인정보 보유 및 파기 */}
          <section>
            <h2 className="mb-4 text-2xl font-bold">개인정보 보유 및 파기</h2>
            <div className="flex flex-col gap-3 text-sm leading-relaxed">
              <p>
                이용자의 개인정보는 서비스 이용 기간 동안 보유하며, 계정 삭제 시 지체 없이 파기합니다. 단, 관계 법령에
                따라 보존이 필요한 경우에는 해당 법령에서 정한 기간 동안 보관합니다.
              </p>
              <ul className="list-disc pl-5 [&_li]:py-1">
                <li>전자상거래 등에서의 소비자 보호에 관한 법률: 계약 또는 청약 철회 기록 5년, 대금 결제 기록 5년</li>
                <li>통신비밀보호법: 접속 로그 기록 3개월</li>
              </ul>
            </div>
          </section>

          {/* 이용자의 권리 */}
          <section>
            <h2 className="mb-4 text-2xl font-bold">이용자의 권리</h2>
            <div className="flex flex-col gap-3 text-sm leading-relaxed">
              <p>이용자는 언제든지 다음과 같은 권리를 행사할 수 있습니다.</p>
              <ul className="list-disc pl-5 [&_li]:py-1">
                <li>개인정보 열람, 정정, 삭제 요청</li>
                <li>개인정보 처리 정지 요청</li>
                <li>동의 철회</li>
              </ul>
              <p>위 권리는 서비스 내 설정 메뉴 또는 이메일({CONTACT_EMAIL})을 통해 행사할 수 있습니다.</p>
            </div>
          </section>

          {/* 계정 및 데이터 삭제 */}
          <section>
            <h2 className="mb-4 text-2xl font-bold">계정 및 데이터 삭제</h2>
            <div className="flex flex-col gap-3 text-sm leading-relaxed">
              <p>
                이용자는 언제든지 계정과 관련 데이터의 삭제를 요청할 수 있습니다. 계정 삭제 시 다음 데이터가 영구적으로
                삭제됩니다.
              </p>
              <ul className="list-disc pl-5 [&_li]:py-1">
                <li>계정 정보 (이메일, 프로필 이름, 프로필 사진)</li>
                <li>학습 데이터 (학습 진도, 테스트 결과, 즐겨찾기)</li>
                <li>구독 정보</li>
              </ul>
              <p className="font-medium">삭제 방법:</p>
              <ul className="list-disc pl-5 [&_li]:py-1">
                <li>앱 내: 설정 → 프로필 → 계정 탈퇴</li>
                <li>
                  이메일 요청:{" "}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline">
                    {CONTACT_EMAIL}
                  </a>
                </li>
              </ul>
              <p>
                계정 삭제 요청은 즉시 처리되며, 삭제 후 데이터는 복구할 수 없습니다. 유료 구독 중인 경우, 계정 삭제 전
                Apple App Store 또는 Google Play에서 구독을 해지해 주시기 바랍니다.
              </p>
            </div>
          </section>

          {/* 만 14세 미만 아동 */}
          <section>
            <h2 className="mb-4 text-2xl font-bold">만 14세 미만 아동</h2>
            <div className="flex flex-col gap-3 text-sm leading-relaxed">
              <p>
                본 서비스는 만 14세 미만 아동의 개인정보를 의도적으로 수집하지 않습니다. 만 14세 미만 아동이 서비스를
                이용한 사실을 확인한 경우, 해당 개인정보를 즉시 삭제합니다.
              </p>
            </div>
          </section>

          {/* 개인정보 보호책임자 */}
          <section>
            <h2 className="mb-4 text-2xl font-bold">문의처</h2>
            <div className="flex flex-col gap-3 text-sm leading-relaxed">
              <p>개인정보 처리에 관한 문의, 불만, 피해 구제는 아래로 연락해 주시기 바랍니다.</p>
              <p>
                이메일:{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline">
                  {CONTACT_EMAIL}
                </a>
              </p>
            </div>
          </section>

          {/* 이 방침의 변경 */}
          <section>
            <h2 className="mb-4 text-2xl font-bold">이 방침의 변경</h2>
            <div className="flex flex-col gap-3 text-sm leading-relaxed">
              <p>
                서비스 제공자는 본 개인정보처리방침을 수시로 변경할 수 있습니다. 변경이 이용자의 개인정보 취급에 중대한
                영향을 미치는 경우, 서비스 제공자는 이용자가 권리를 행사할 수 있도록 충분한 사전 통지를 제공합니다.
              </p>
            </div>
          </section>
        </div>

        {/* 방침 변경 일자 */}
        <p className="mt-10 text-sm text-muted-foreground">이 방침은 2026년 04월 15일부터 시행합니다.</p>
      </main>
    </Screen>
  );
}
